import { Draggable } from "@hello-pangea/dnd";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { useUpdateDashboardMutation } from "../../../api/network/overviewApiServices";
import { useLazyGetReportSpecificFieldsQuery } from "../../../api/network/reportApiServices";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  CustomWidget,
  DASHBOARD_WIDGET_TYPE,
  DASHBOARD_WIDGET_TYPE_W_DETAILS_OPTION,
  DashboardWidget,
} from "../../../api/types/Overview";
import { TReport } from "../../../api/types/Report";
import svgIcons from "../../../assets/svg/svgIcons";
import ErrorBoundaryForSection from "../../../components/errorBoundaryForSection";
import { APP_CONFIG, REPORT_TYPES } from "../../../constants/constants";
import { useReportUtils } from "../../reports/hooks/reportUtils";
import { useOverviewContext } from "../context/overview.context";
import { onPrint } from "../utils/overview";
import WidgetAssetAvailability from "../widgets/widgetAssetAvailaility";
import WidgetAssetMaintenance from "../widgets/widgetAssetMaintenance";
import WidgetAssetStatusReport from "../widgets/widgetAssetStatusReport";
import WidgetCustom from "../widgets/widgetCustom";
import WidgetFleetFuelSummary from "../widgets/widgetFleetFuelSummary";
import WidgetFuelEfficiency from "../widgets/widgetFuelEfficiency";
import WidgetFuelEnergyConsumption from "../widgets/widgetFuelEnergyConsumption";
import WidgetFuelIdlingRatio from "../widgets/widgetFuelIdlingRatio";
import WidgetMaintenanceDue from "../widgets/widgetMaintenanceDue";
import WidgetSafetyEvents from "../widgets/widgetSafetyEvents";
import WidgetSafetyEventsAsset from "../widgets/widgetSafetyEventsAsset";
import WidgetSafetyEventsDriver from "../widgets/widgetSafetyEventsDriver";
import WidgetsKeyPerformanceIndi from "../widgets/widgetsKeyPerformanceIndi";
import WidgetsVehiclesEffiComparisons from "../widgets/widgetsVehiclesEffiComparisons";

interface IColumnItemProps {
  widget: DashboardWidget;
  index: number;
}

export interface IWidgetProps {
  widgetType: DASHBOARD_WIDGET_TYPE;
}

const OverviewDragDropColumnItem: FC<IColumnItemProps> = ({
  widget,
  index,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.widget_titles",
  });
  const { t: tAddWidgets } = useTranslation("translation", {
    keyPrefix: "overviewScreen.add_widgets",
  });
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const [showDetails, setShowDetails] = useState(false);

  const {
    refetchDashboardList,
    dashList,
    selectedDashboard,
    dashboardColumns,
    isFetchingDashboardList,
    setDashboardColumns,
    timeFilter,
    isLoadingUpdateDashboard,
    customList,
  } = useOverviewContext();

  const { navigateToReportPage } = useReportUtils();

  // dashboard mutation
  const [
    updateDashboardAPITrigger,
    { isLoading: isLoadingUpdateDashboard_thisComponent },
  ] = useUpdateDashboardMutation();

  const widgetSlug = `${widget.dashboard}__${widget.id}__${widget.type}`;
  const widgetLabel =
    widget?.category?.toString() === "2"
      ? widget?.custom_widget_title
      : !!widget.name
        ? t(`enum_${widget.name}`)
        : "";

  // edit custom widget
  const [
    getCustomWidgetReportSpecificFieldsForEdit,
    { isLoading: isLoadingCustomWidgetReportSpecificFieldsForEdit },
  ] = useLazyGetReportSpecificFieldsQuery();
  const handleEditCustomWidget = async (widget: DashboardWidget) => {
    if (!selectedDashboard) {
      toast.error(tAddWidgets("toast.dashboard_not_selected"));
      return;
    }
    if (!widget.custom_widget) {
      toast.error(tAddWidgets("toast.custom_widget_not_found"));
      return;
    }
    const customWidget = customList?.find(
      (item) => item.id === widget.custom_widget
    );
    const customReportSpecificFields =
      await getCustomWidgetReportSpecificFieldsForEdit({
        type: REPORT_TYPES.CUSTOM,
        report_id: customWidget?.report ?? 0,
      });
    if (!customWidget || !customReportSpecificFields.data) {
      toast.error(tAddWidgets("toast.custom_widget_report_not_found"));
      return;
    }
    dispatch(
      setModalsData({
        ...modalsState,
        showUseCustomReport: true,
        customWidgetToEdit: customWidget, // this is used for editing custom widget
        customWidgetReport: customReportSpecificFields.data as TReport,
      })
    );
  };

  // remove widget from dashboard
  const handleRemoveWidget = () => {
    if (!selectedDashboard) {
      toast.error(t("toast.dashboard_not_selected"));
      return;
    }

    const dashboardColumnsCopy = JSON.parse(JSON.stringify(dashboardColumns));
    dashboardColumnsCopy.col1.colWidgets =
      dashboardColumns.col1.colWidgets.filter((w) => w.id !== widget.id);
    dashboardColumnsCopy.col2.colWidgets =
      dashboardColumns.col2.colWidgets.filter((w) => w.id !== widget.id);
    dashboardColumnsCopy.col3.colWidgets =
      dashboardColumns.col3.colWidgets.filter((w) => w.id !== widget.id);
    setDashboardColumns(dashboardColumnsCopy);

    const thisDashboard = dashList?.find((d) => d.id === selectedDashboard);

    // find column with least widgets
    const columns = [1, 2, 3];
    const columnCounts = columns.map(
      (col) =>
        thisDashboard?.widgets?.filter((w) => w.page_column === col).length || 0
    );
    const leastWidgetsColumnIndex = columnCounts.indexOf(
      Math.min(...columnCounts)
    );
    const leastWidgetsColumn = columns[leastWidgetsColumnIndex];
    console.log("leastWidgetsColumn", leastWidgetsColumn);

    updateDashboardAPITrigger({
      id: selectedDashboard,
      widgets: [
        ...(thisDashboard?.widgets?.filter((w) => w.id !== widget.id) || []),
      ],
    })
      .unwrap()
      .then((data) => {
        toast.success(tAddWidgets("toast.remove_success"), {
          autoClose: 2000,
          position: "top-center",
        });
        refetchDashboardList();
        // hideModal(); don't close the modal
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.add_failed")];
        toast.error(errors?.join(" "));
      })
      .finally(() => {
        // setSubmitting(false);
      });
  };

  const handleWidgetPrint = () => {
    dispatch(setModalsData({ ...modalsState, widgetSelectedForPrint: widget }));
    setTimeout(() => {
      onPrint();
    }, 1000);
  };

  const isCustomWidget =
    widget?.category && widget.category?.toString() === "2";

  //Temporary disable of widgets
  const disabledWidgets = ["Fuel Efficiency", "Key Performance Indicators", "Vehicle Efficiency Comparisons", "Asset Health", "Driver Performance", "Key Performance Indicators", "Fleet Fuel Summary"];
  if(widgetLabel && disabledWidgets.includes(widgetLabel))
    return;
  else

  return (
    <Draggable draggableId={widgetSlug} index={index}>
      {(provided, snapshot) => (
        <div
          className={
            `bg-white rounded-lg shadow-md border border-black/10 p-4 mb-4 transition-all` +
            `${modalsState.showDashboardAddWidget ? " z-dashboard-widget" : ""}` +
            `${!!modalsState.widgetSelectedForPrint ? (modalsState?.widgetSelectedForPrint?.id === widget.id ? " print-widget min-h-96 fixed top-[3vw] left-[3vw] w-[94vw] z-popup" : "non-print-widget hidden") : ""}`
          }
          ref={provided.innerRef}
          {...provided.draggableProps}
          // {...provided.dragHandleProps}
          key={`${widgetSlug}__${index}__${timeFilter}`}
        >
          {/* Widget Header */}
          <h4 className="relative flex justify-between items-start gap-2 font-semibold">
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <pre className="absolute text-[8px] bottom-full">
                id: {widget.id}
                <span className="inline-block ps-6 text-amber-700">
                  {!!widget.custom_widget
                    ? `custom_widget: ${widget.custom_widget}`
                    : ""}
                </span>
                <br />
                widgetSlug: {widgetSlug}
                <br />
                widget type: {widget.type}
              </pre>
            )}
            {/* Widget Title */}
            <span
              className="flex-grow min-h-9 flex items-center relative Xleading-4 Xoverflow-hidden Xwhitespace-nowrap Xtext-ellipsis"
              title={widgetLabel}
            >
              <span className="text-sm leading-4 line-clamp-3">
                {widget?.category?.toString() === "2"
                  ? widget?.custom_widget_title
                  : widgetLabel}
              </span>
              {!!modalsState.widgetSelectedForPrint &&
                modalsState?.widgetSelectedForPrint?.id === widget.id && (
                  // Print mode overlay to hide selected widget and show only on print preview
                  <span className="no-print absolute top-0 left-0 right-0 bottom-0 bg-white z-10 pointer-events-none touch-none"></span>
                )}
            </span>

            {/* Cancel Print Mode */}
            {!!modalsState.widgetSelectedForPrint && (
              <button
                type="button"
                className="no-print absolute z-50 top-48 left-1/2 -translate-x-1/2 -translate-y-1/2 flex p-2 enabled:bg-field-error-dark/20 disabled:bg-gray-300 rounded-full group enabled:hover:bg-field-error-dark enabled:text-field-error-dark disabled:text-heading-gray888 enabled:hover:text-white"
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      widgetSelectedForPrint: null,
                    })
                  );
                }}
              >
                <span className="text-sm font-semibold px-2">
                  {t("close_print_mode")}
                </span>
                <img
                  src={svgIcons.CloseIcon}
                  alt="close print more"
                  className="xl:hidden size-5 opacity-50 group-enabled:group-hover:opacity-100 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                />
              </button>
            )}

            {/* Widget Actions */}
            {!modalsState.widgetSelectedForPrint && (
              <span
                className={`no-print flex${modalsState.showDashboardAddWidget ? " flex-row-reverse" : ""} gap-2 shrink-0`}
              >
                {modalsState.showDashboardAddWidget ? (
                  <>
                    {/* Remove Widget Button */}
                    <button
                      type="button"
                      className="flex p-2 enabled:bg-field-error-dark/20 disabled:bg-gray-300 rounded-full group enabled:hover:bg-field-error-dark enabled:text-field-error-dark disabled:text-heading-gray888 enabled:hover:text-white"
                      onClick={handleRemoveWidget}
                      disabled={
                        isFetchingDashboardList || isLoadingUpdateDashboard
                      }
                    >
                      <span className="hidden xl:block text-sm font-normal px-1">
                        {tAddWidgets("remove")}
                      </span>
                      <img
                        src={svgIcons.CloseIcon}
                        alt="remove"
                        className="xl:hidden size-5 opacity-50 group-enabled:group-hover:opacity-100 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                      />
                    </button>
                    {/* Edit Widget Button */}
                    {!!widget?.custom_widget && (
                      <button
                        type="button"
                        className="flex p-2 enabled:bg-accent-blue-pale disabled:bg-gray-100 rounded-full group enabled:hover:bg-accent-blue-dark enabled:text-accent-blue-dark disabled:text-heading-gray888 enabled:hover:text-white"
                        onClick={() => handleEditCustomWidget(widget)}
                        disabled={
                          isFetchingDashboardList ||
                          isLoadingUpdateDashboard ||
                          isLoadingCustomWidgetReportSpecificFieldsForEdit
                        }
                      >
                        <span className="hidden xl:block text-sm font-normal px-1">
                          {tAddWidgets("edit")}
                        </span>
                        <img
                          src={
                            isLoadingCustomWidgetReportSpecificFieldsForEdit
                              ? svgIcons.LoadingAnimation
                              : svgIcons.EditIcon
                          }
                          alt="edit"
                          className="xl:hidden size-5 opacity-50 group-enabled:group-hover:opacity-100 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                        />
                      </button>
                    )}
                    {/* Placeholder drag handle - can stay hidden but be present in DOM */}
                    <span
                      className="absolute size-0"
                      {...provided.dragHandleProps}
                    ></span>
                  </>
                ) : (
                  <>
                    {/* Details Button */}
                    {
                      // either widget has name and is in the list of widgets with details option
                      ((!!widget.name &&
                        DASHBOARD_WIDGET_TYPE_W_DETAILS_OPTION.includes(
                          widget.name
                        )) ||
                        // or it is a custom widget
                        isCustomWidget) && (
                        <button
                          type="button"
                          className="flex p-2 bg-accent-blue-pale rounded-full group enabled:hover:bg-accent-blue-dark enabled:hover:text-white"
                          onClick={() => {
                            if (isCustomWidget) {
                              const reportId = customList?.find(
                                (c) => c.id === widget.custom_widget
                              )?.report as number;
                              if (!reportId) {
                                console.log(
                                  "No report found for custom widget"
                                );
                                return;
                              }
                              navigateToReportPage(
                                reportId,
                                REPORT_TYPES.CUSTOM
                              );
                            } else {
                              setShowDetails(true);
                            }
                          }}
                        >
                          <span className="hidden xl:block text-sm font-normal px-1">
                            {t("view_details")}
                          </span>
                          <img
                            src={svgIcons.MagnifierIcon}
                            alt="print"
                            className="xl:hidden size-5 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                          />
                        </button>
                      )
                    }
                    {/* Print Button */}
                    <button
                      className="flex p-2 bg-accent-blue-pale rounded-full group enabled:hover:bg-accent-blue-dark"
                      onClick={handleWidgetPrint}
                      type="button"
                    >
                      <img
                        src={svgIcons.PrintIcon}
                        alt="print"
                        className="size-5 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                      />
                    </button>
                    {/* Drag Handle */}
                    <span className="group" {...provided.dragHandleProps}>
                      <button
                        className={`flex p-2 ${snapshot.isDragging ? "bg-accent-blue-dark" : "bg-accent-blue-pale"} rounded-full group group-hover:enabled:bg-accent-blue-dark group-mo pointer-events-none touch-none`}
                      >
                        <img
                          src={svgIcons.HandIcon}
                          alt="hand"
                          className={`size-5 ${snapshot.isDragging ? "grayscale-0 brightness-0 invert" : "group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert"}`}
                        />
                      </button>
                    </span>
                  </>
                )}
              </span>
            )}
          </h4>

          {/* Widget Content */}
          <span
            className={`relative ${modalsState.showDashboardAddWidget ? "opacity-40 pointer-events-none touch-none" : ""}`}
          >
            <ErrorBoundaryForSection
              wrapClass="w-full h-full"
              fontSize="compact"
            >
              {!!modalsState.widgetSelectedForPrint &&
                modalsState?.widgetSelectedForPrint?.id === widget.id && (
                  // Print mode overlay to hide selected widget and show only on print preview
                  <span className="no-print absolute top-0 left-0 right-0 bottom-0 bg-white z-20 pointer-events-none touch-none"></span>
                )}
              {!!widget.name &&
              [
                DASHBOARD_WIDGET_TYPE.MAINTENANCE_DUE_HOURS,
                DASHBOARD_WIDGET_TYPE.MAINTENANCE_DUE_MILES,
                DASHBOARD_WIDGET_TYPE.MAINTENANCE_DUE_PERIOD,
              ].includes(widget.name) ? (
                <WidgetMaintenanceDue
                  widgetType={widget.name}
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              ) : null}
              {widget.name === DASHBOARD_WIDGET_TYPE.ASSET_MAINTENANCE && (
                <WidgetAssetMaintenance widgetType={widget.name} />
              )}
              {widget.name === DASHBOARD_WIDGET_TYPE.ASSET_STATUS && (
                <WidgetAssetStatusReport widgetType={widget.name} />
              )}
              
              {/* //temporary disable
              {widget.name === DASHBOARD_WIDGET_TYPE.ASSET_AVAILABILITY && (
                <WidgetAssetAvailability widgetType={widget.name} />
              )} */}
              {widget.name ===
                DASHBOARD_WIDGET_TYPE.FUEL_ENERGY_CONSUMPTION && (
                <WidgetFuelEnergyConsumption
                  widgetType={widget.name}
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              )}
       
              {/* //temporary disable
                {widget.name ===
                DASHBOARD_WIDGET_TYPE.VEHICLE_EFFICIENCY_COMPARISON && (
                <WidgetsVehiclesEffiComparisons widgetType={widget.name} />
              )} 
              {widget.name ===
                DASHBOARD_WIDGET_TYPE.KEY_PERFORMANCE_INDICATORS && (
                <WidgetsKeyPerformanceIndi widgetType={widget.name} />
              )} */}
              {widget.name === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS && (
                <WidgetSafetyEvents widgetType={widget.name} />
              )}
              {widget.name === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS_ASSET && (
                <WidgetSafetyEventsAsset
                  widgetType={widget.name}
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              )}
              
              {/* //temporary disable
              {widget.name === DASHBOARD_WIDGET_TYPE.SAFETY_EVENTS_DRIVER && (
                <WidgetSafetyEventsDriver
                  widgetType={widget.name}
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              )}
                {widget.name === DASHBOARD_WIDGET_TYPE.FLEET_FUEL_SUMMARY && (
                <WidgetFleetFuelSummary widgetType={widget.name} />
              )}
              {(widget.name === DASHBOARD_WIDGET_TYPE.ASSET_FUEL_EFFICIENCY ||
                widget.name ===
                  DASHBOARD_WIDGET_TYPE.DRIVER_FUEL_EFFICIENCY) && (
                <WidgetFuelEfficiency
                  widgetType={widget.name}
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              )} */}
              {(widget.name === DASHBOARD_WIDGET_TYPE.ASSET_IDLING_RATIO ||
                widget.name === DASHBOARD_WIDGET_TYPE.DRIVER_IDLING_RATIO) && (
                <WidgetFuelIdlingRatio
                  widgetType={widget.name}
                  showDetails={showDetails}
                  setShowDetails={setShowDetails}
                />
              )}
              {/* Custom Widgets */}
              {isCustomWidget && (
                <ErrorBoundaryForSection>
                  <WidgetCustom
                    widgetDetails={widget as unknown as CustomWidget}
                    widgetType={widget.name ?? DASHBOARD_WIDGET_TYPE.CUSTOM}
                    showDetails={showDetails}
                    setShowDetails={setShowDetails}
                  />
                </ErrorBoundaryForSection>
              )}
              {/* Dashboard Widgets - Initial UI */}
              {/* {widgetSlug === EWidget Types.ASSET_EVENTS && (
            <WidgetAssetEvents />
          )}
          {widgetSlug === EWidget Types.ASSET_STATUS_REPORT && (
            <WidgetAssetStatusReport />
          )}
          {widgetSlug === EWidget Types.MAINTENANCE_OVERDUE && (
            <WidgetMaintenanceDue />
          )}
          {widgetSlug === EWidget Types.SAFETY_EVENTS && (
            <WidgetSafetyEvents />
          )} */}
            </ErrorBoundaryForSection>
          </span>
        </div>
      )}
    </Draggable>
  );
};

export default OverviewDragDropColumnItem;
